import { ArrowLeft, Monitor, Smartphone, Code, Send, Copy, RotateCcw, Save, Mail } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { EmailTabNav } from "@/components/admin/email/EmailTabNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import type { EmailTemplateConfig, VariableSchema } from "@/types/email";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

type ViewMode = "desktop" | "mobile" | "html";

export default function AdminEmailPreview() {
  const { key } = useParams<{ key: string }>();
  const { toast } = useToast();
  const [variables, setVariables] = useState<Record<string, unknown>>({});
  const [viewMode, setViewMode] = useState<ViewMode>("desktop");
  const [showSendOther, setShowSendOther] = useState(false);
  const [otherEmail, setOtherEmail] = useState("");

  // Template config
  const { data: template } = useQuery({
    queryKey: ["email-template", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates_config")
        .select("*")
        .eq("template_key", key)
        .single();
      if (error) throw error;
      return data as EmailTemplateConfig;
    },
    enabled: !!key,
  });

  // Init variables from preview_defaults
  useEffect(() => {
    if (template?.preview_defaults) {
      setVariables(template.preview_defaults as Record<string, unknown>);
    }
  }, [template]);

  const debouncedVars = useDebounce(variables, 500);

  // Render preview
  const { data: previewHtml, isLoading: previewLoading } = useQuery({
    queryKey: ["email-preview-render", key, debouncedVars],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("email-preview", {
        body: { templateKey: key, variables: debouncedVars },
      });
      if (error) throw error;
      return data?.html as string | null;
    },
    enabled: !!key && !!template?.is_implemented,
  });

  // Send test
  const sendTestMutation = useMutation({
    mutationFn: async (targetEmail?: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      const email = targetEmail || user?.email;
      if (!email) throw new Error("Não foi possível determinar o email");

      const { data, error } = await supabase.functions.invoke("send-manual-email", {
        body: {
          templateKey: key,
          recipientEmail: email,
          recipientName: "Teste Admin",
          recipientType: "admin",
          variables: debouncedVars,
          isTest: true,
        },
      });
      if (error) throw error;
      return { ...data, email };
    },
    onSuccess: (data) => {
      toast({ title: `Email de teste enviado para ${data.email}` });
    },
    onError: (err) => {
      toast({ title: "Erro ao enviar teste", description: String(err), variant: "destructive" });
    },
  });

  // Save defaults
  const saveDefaultsMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("email_templates_config")
        .update({ preview_defaults: variables, updated_at: new Date().toISOString() })
        .eq("template_key", key);
      if (error) throw error;
    },
    onSuccess: () => toast({ title: "Dados padrão salvos" }),
  });

  const updateVar = useCallback((name: string, value: unknown) => {
    setVariables((prev) => ({ ...prev, [name]: value }));
  }, []);

  const schema = template?.variables_schema ?? {};
  const schemaEntries = Object.entries(schema) as [string, VariableSchema][];

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <EmailTabNav />

      <Link to={`/admin/emails/templates/${key}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> {template?.display_name || key}
      </Link>

      <div className="flex h-[calc(100vh-180px)] gap-0 border border-border rounded-2xl overflow-hidden bg-card">
        {/* Left panel: Variables form (40%) */}
        <div className="w-[40%] border-r border-border overflow-y-auto p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Variáveis</h2>
            <p className="text-sm text-muted-foreground">{template?.display_name}</p>
          </div>

          {schemaEntries.length > 0 ? (
            <div className="space-y-4">
              {schemaEntries.map(([name, fieldSchema]) => (
                <div key={name} className="space-y-1.5">
                  <label className="text-sm font-medium flex items-center gap-1">
                    {fieldSchema.label || name}
                    {fieldSchema.required && <span className="text-red-500">*</span>}
                  </label>
                  {fieldSchema.type === "boolean" ? (
                    <Switch
                      checked={Boolean(variables[name])}
                      onCheckedChange={(v) => updateVar(name, v)}
                    />
                  ) : fieldSchema.type === "text" ? (
                    <textarea
                      rows={3}
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                      value={String(variables[name] ?? "")}
                      onChange={(e) => updateVar(name, e.target.value)}
                    />
                  ) : fieldSchema.type === "select" && fieldSchema.options ? (
                    <select
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                      value={String(variables[name] ?? "")}
                      onChange={(e) => updateVar(name, e.target.value)}
                    >
                      {fieldSchema.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <Input
                        type={fieldSchema.type === "number" ? "number" : fieldSchema.type === "email" ? "email" : fieldSchema.type === "url" ? "url" : "text"}
                        value={String(variables[name] ?? "")}
                        onChange={(e) => updateVar(name, fieldSchema.type === "number" ? Number(e.target.value) : e.target.value)}
                        className={`${fieldSchema.type === "uuid" ? "font-mono" : ""} ${fieldSchema.required && !variables[name] ? "border-red-300 focus:border-red-500" : ""}`}
                      />
                      {fieldSchema.required && !variables[name] && (
                        <p className="text-[10px] text-red-500">Campo obrigatório</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Este template não possui variáveis configuradas.</p>
          )}

          {/* Footer fixo */}
          <div className="sticky bottom-0 bg-card border-t border-border pt-4 flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (template?.preview_defaults) setVariables(template.preview_defaults as Record<string, unknown>);
              }}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Restaurar
            </Button>
            <Button variant="outline" size="sm" onClick={() => saveDefaultsMutation.mutate()}>
              <Save className="w-4 h-4 mr-1" /> Salvar padrão
            </Button>
          </div>
        </div>

        {/* Right panel: Render (60%) */}
        <div className="w-[60%] flex flex-col bg-muted/30">
          {/* Toolbar */}
          <div className="flex items-center justify-between p-3 border-b border-border bg-card">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(v) => v && setViewMode(v as ViewMode)}
              className="bg-muted p-1 rounded-lg"
            >
              <ToggleGroupItem value="desktop" className="text-xs px-3 rounded-md data-[state=on]:bg-card data-[state=on]:shadow-sm">
                <Monitor className="w-3.5 h-3.5 mr-1" /> Desktop
              </ToggleGroupItem>
              <ToggleGroupItem value="mobile" className="text-xs px-3 rounded-md data-[state=on]:bg-card data-[state=on]:shadow-sm">
                <Smartphone className="w-3.5 h-3.5 mr-1" /> Mobile
              </ToggleGroupItem>
              <ToggleGroupItem value="html" className="text-xs px-3 rounded-md data-[state=on]:bg-card data-[state=on]:shadow-sm">
                <Code className="w-3.5 h-3.5 mr-1" /> HTML
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="flex gap-2">
              {previewHtml && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(previewHtml);
                    toast({ title: "HTML copiado" });
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copiar
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendTestMutation.mutate()}
                disabled={sendTestMutation.isPending}
              >
                <Send className="w-4 h-4 mr-1" />
                {sendTestMutation.isPending ? "Enviando..." : "Enviar teste"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSendOther(true)}
              >
                <Mail className="w-4 h-4 mr-1" /> Outro email
              </Button>
            </div>
          </div>

          {/* Render area */}
          <div className="flex-1 overflow-auto p-6 flex justify-center items-start">
            {!template?.is_implemented ? (
              <div className="flex flex-col items-center justify-center text-muted-foreground py-16">
                <Code className="w-7 h-7 mb-3" />
                <p className="font-medium">Template não implementado</p>
                <p className="text-xs">Preview usa dados de exemplo para referência</p>
              </div>
            ) : previewLoading ? (
              <div className="w-full max-w-[600px] h-[500px] bg-card animate-pulse rounded-xl" />
            ) : viewMode === "html" ? (
              <pre className="bg-foreground text-background font-mono text-xs p-4 rounded-xl overflow-auto w-full max-h-full">
                {previewHtml || "Nenhum HTML gerado"}
              </pre>
            ) : previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                sandbox="allow-same-origin"
                className="bg-white shadow-lg rounded-sm border border-border transition-all"
                style={{ width: viewMode === "desktop" ? 600 : 375, minHeight: 500 }}
                title="Email preview"
              />
            ) : (
              <p className="text-muted-foreground">Erro ao renderizar preview</p>
            )}
          </div>
        </div>
      </div>

      {/* Dialog: Enviar para outro email */}
      <Dialog open={showSendOther} onOpenChange={setShowSendOther}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enviar teste para outro email</DialogTitle>
          </DialogHeader>
          <Input
            type="email"
            placeholder="email@exemplo.com"
            value={otherEmail}
            onChange={(e) => setOtherEmail(e.target.value)}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSendOther(false)}>Cancelar</Button>
            <Button
              disabled={!otherEmail || sendTestMutation.isPending}
              onClick={() => { sendTestMutation.mutate(otherEmail); setShowSendOther(false); setOtherEmail(""); }}
            >
              <Send className="w-4 h-4 mr-2" /> Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
